import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { History } from 'lucide-react';

const ActionHistoryTab = ({ isLoading, setIsLoading, currentUser, showNotification }) => {
  // Action history specific states
  const [actionHistory, setActionHistory] = useState([]);
  const [actionHistoryPagination, setActionHistoryPagination] = useState({ page: 0, rowsPerPage: 20, count: 0 });
  const [actionHistoryFilters, setActionHistoryFilters] = useState({ type: 'all', user: '' });

  // Animation variant
  const slideUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.5, 
        ease: "easeOut" 
      } 
    }
  };

  // Load action history on component mount
  useEffect(() => {
    if (currentUser) {
      fetchActionHistory();
    }
  }, [currentUser]);

  // Get action history
  const fetchActionHistory = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: actionHistoryPagination.page,
        limit: actionHistoryPagination.rowsPerPage
      };
      
      // Add filters if defined
      if (actionHistoryFilters.type !== 'all') {
        params.type = actionHistoryFilters.type;
      }
      
      if (actionHistoryFilters.user) {
        params.user = actionHistoryFilters.user;
      }
      
      const response = await axios.get('/api/admin/action-history', { params });
      setActionHistory(response.data.actions);
      setActionHistoryPagination({
        ...actionHistoryPagination,
        count: response.data.total
      });
    } catch (error) {
      console.error('Error al obtener historial de acciones:', error);
      showNotification('Error al cargar historial de acciones', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Change page or rows per page
  const handleActionHistoryPaginationChange = (event, newPage) => {
    setActionHistoryPagination({
      ...actionHistoryPagination,
      page: newPage
    });
    fetchActionHistory();
  };
  
  const handleActionHistoryRowsPerPageChange = (event) => {
    setActionHistoryPagination({
      ...actionHistoryPagination,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0
    });
    fetchActionHistory();
  };
  
  // Apply filters to history
  const handleApplyHistoryFilters = () => {
    setActionHistoryPagination({
      ...actionHistoryPagination,
      page: 0
    });
    fetchActionHistory();
  };

  return (
    <motion.div 
      className="bg-white rounded-xl shadow-lg overflow-hidden"
      variants={slideUp}
    >
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center gap-2 mb-6">
          <History className="w-5 h-5 text-indigo-600" />
          <h2 className="text-xl font-semibold text-gray-800">Historial de Acciones</h2>
        </div>
        
        <div className="flex flex-col md:flex-row flex-wrap gap-4 mb-4">
          <div className="w-full md:w-auto">
            <label htmlFor="actionType" className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de acción
            </label>
            <select
              id="actionType"
              value={actionHistoryFilters.type}
              onChange={(e) => setActionHistoryFilters({...actionHistoryFilters, type: e.target.value})}
              className="w-full md:w-48 px-3 py-2 bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                      focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">Todas las acciones</option>
              <option value="USUARIO">Usuarios</option>
              <option value="PROPIEDAD">Propiedades</option>
              <option value="REPORTE">Reportes</option>
              <option value="EMAIL">Emails</option>
            </select>
          </div>
          
          <div className="w-full md:w-auto">
            <label htmlFor="staffUser" className="block text-sm font-medium text-gray-700 mb-1">
              Usuario Staff
            </label>
            <input
              id="staffUser"
              type="text"
              placeholder="ID o Nombre"
              value={actionHistoryFilters.user}
              onChange={(e) => setActionHistoryFilters({...actionHistoryFilters, user: e.target.value})}
              className="w-full md:w-48 px-3 py-2 bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                      placeholder-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          
          <div className="w-full md:w-auto flex items-end">
            <motion.button
              onClick={handleApplyHistoryFilters}
              className="w-full md:w-auto px-6 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-lg shadow-md"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              Aplicar Filtros
            </motion.button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-indigo-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Fecha</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Usuario Staff</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Acción</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Objetivo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Detalles</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {actionHistory.length > 0 ? (
              actionHistory.map((action) => (
                <tr key={action.id} className="hover:bg-indigo-50/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{action.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{new Date(action.fecha).toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{action.staff_nombre || action.staff_id}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      action.accion.includes('ELIMINA') ? 'bg-red-100 text-red-800' :
                      action.accion.includes('ACTUALIZA') ? 'bg-blue-100 text-blue-800' :
                      action.accion.includes('CREA') ? 'bg-green-100 text-green-800' :
                      'bg-indigo-100 text-indigo-800'
                    }`}>
                      {action.accion}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {action.target_type}: {action.target_id}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate">
                    <div 
                      className="hover:bg-indigo-50 px-2 py-1 rounded cursor-help transition-colors"
                      title={action.detalles}
                    >
                      {action.detalles.substring(0, 50)}...
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  No hay registros de acciones disponibles
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {actionHistoryPagination.count > 0 ? (
            <span>
              Mostrando {actionHistoryPagination.page * actionHistoryPagination.rowsPerPage + 1} a{' '}
              {Math.min((actionHistoryPagination.page + 1) * actionHistoryPagination.rowsPerPage, actionHistoryPagination.count)} de{' '}
              {actionHistoryPagination.count} acciones
            </span>
          ) : (
            <span>No hay registros de acciones disponibles</span>
          )}
        </div>
        
        <div className="flex gap-2">
          <select
            value={actionHistoryPagination.rowsPerPage}
            onChange={(e) => handleActionHistoryRowsPerPageChange(e)}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value={10}>10 / página</option>
            <option value={20}>20 / página</option>
            <option value={50}>50 / página</option>
            <option value={100}>100 / página</option>
          </select>
          
          <div className="flex">
            <button
              onClick={() => handleActionHistoryPaginationChange(null, actionHistoryPagination.page - 1)}
              disabled={actionHistoryPagination.page === 0}
              className={`px-3 py-1 border ${
                actionHistoryPagination.page === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-indigo-600 hover:bg-indigo-50'
              } rounded-l-lg transition-colors`}
            >
              Anterior
            </button>
            <button
              onClick={() => handleActionHistoryPaginationChange(null, actionHistoryPagination.page + 1)}
              disabled={actionHistoryPagination.page >= Math.ceil(actionHistoryPagination.count / actionHistoryPagination.rowsPerPage) - 1}
              className={`px-3 py-1 border border-l-0 ${
                actionHistoryPagination.page >= Math.ceil(actionHistoryPagination.count / actionHistoryPagination.rowsPerPage) - 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-indigo-600 hover:bg-indigo-50'
              } rounded-r-lg transition-colors`}
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ActionHistoryTab;